'use client';
import { Box, SxProps } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';

import SliderHandler from './SliderHandler';
import Optional from '../../Optional';
import { TBImgType } from '../../../utils/mappers/image';

type Props = {
  beforeImage?: TBImgType;
  afterImage?: TBImgType;
  sx?: SxProps;
  imgStyle?: Record<string, any>;
  sizes?: string;
};

const INIT_SLIDER = 33;

const SliderImage = ({
  beforeImage,
  afterImage,
  sx,
  imgStyle,
  sizes,
}: Props) => {
  const [sliderVal, setSliderVal] = useState(INIT_SLIDER);
  const containerRef = useRef<HTMLDivElement>(null);

  const hasBoth = !!beforeImage?.url && !!afterImage?.url;
  const image = beforeImage?.url ? beforeImage : afterImage;

  useEffect(() => {
    setSliderVal(INIT_SLIDER);
  }, [image?.url]);

  if (!image) return null;
  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        borderRadius: { xs: 0.5, md: 1 },
        ...sx,
      }}
    >
      <Image
        src={afterImage?.url || ''}
        alt={afterImage?.altText || ''}
        placeholder={image.blurDataUrl ? 'blur' : 'empty'}
        blurDataURL={image.blurDataUrl}
        fill
        sizes="100vw"
        style={{
          objectFit: 'cover',
          pointerEvents: hasBoth ? 'none' : undefined,
          ...imgStyle,
        }}
      />

      <Optional condition={hasBoth}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            clipPath: `inset(0 ${100 - sliderVal}% 0 0)`,
          }}
        >
          <Image
            src={image.url || ''}
            alt={image.altText || ''}
            fill
            sizes={sizes || '100vw'}
            placeholder={image.blurDataUrl ? 'blur' : 'empty'}
            blurDataURL={image.blurDataUrl}
            style={{
              objectFit: 'cover',
              pointerEvents: 'none',
              filter: hasBoth ? `grayscale(80%)` : undefined,
              ...imgStyle,
            }}
          />
        </Box>

        <SliderHandler
          value={sliderVal}
          onChange={setSliderVal}
          containerRef={containerRef}
        />
      </Optional>
    </Box>
  );
};

export default SliderImage;
